/**
 * 8. Global Search Results — the records GET /v1/search finds, grouped by
 * kind, with a module filter.
 *
 * The response (D432): the envelope's `data` is the array of groups and the
 * total is `meta.total` beside it, so the page reads it with `useApiList`
 * (items = groups, total = meta.total), as the other list pages do. The
 * module filter offers exactly the entities the API searches
 * (`SEARCHABLE` in apps/api/src/kernel/search/search-query.service.ts).
 *
 * Arabic and English (C7): the module filter names each kind of record in
 * the active language (the sidebar's words), a group is headed by its kind,
 * and a status is the glossary's word for that kind of record.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Input,
  Badge,
  EmptyState,
  Skeleton,
} from "@/components/ui";
import { QueryError } from "@/components/QueryError";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation, type MessageKey } from "@/lib/i18n";
import { recordTypeLabel, statusLabel } from "@/lib/i18n/records";

interface SearchResultGroup {
  entityType: string;
  results: Array<{
    id: string;
    title: string;
    subtitle?: string;
    status?: string;
  }>;
}

/**
 * The module filter: the API's entity name, and the sidebar's word for it.
 * Only what GET /v1/search searches; a choice the API does not serve would
 * always answer "no results".
 */
const MODULES: Array<{ value: string; label: MessageKey }> = [
  { value: "Customer", label: "nav.customers" },
  { value: "Supplier", label: "nav.suppliers" },
  { value: "Product", label: "nav.products" },
  { value: "SalesOrder", label: "nav.salesOrders" },
  { value: "PurchaseOrder", label: "nav.purchaseOrders" },
  { value: "JournalEntry", label: "nav.journalEntries" },
];

export function SearchResultsPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [entity, setEntity] = useState("");
  const { data, isLoading, isFetching, error, refetch } =
    useApiList<SearchResultGroup>(
      ["search", query, entity],
      `/search?q=${encodeURIComponent(query)}${entity ? `&entity=${entity}` : ""}`,
      { enabled: query.trim().length > 0 },
    );

  return (
    <div>
      <PageHeader title={t("search.title")} subtitle={t("search.subtitle")} />
      <div
        style={{
          display: "flex",
          gap: 10,
          maxInlineSize: 640,
          marginBlockEnd: 16,
        }}
      >
        <Input
          placeholder={t("search.placeholder")}
          aria-label={t("search.title")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className="atlas-select"
          style={{ inlineSize: 180 }}
          aria-label={t("search.module")}
          value={entity}
          onChange={(e) => setEntity(e.target.value)}
        >
          <option value="">{t("search.allModules")}</option>
          {MODULES.map((m) => (
            <option key={m.value} value={m.value}>
              {t(m.label)}
            </option>
          ))}
        </select>
      </div>

      {!query ? (
        <EmptyState
          title={t("search.emptyTitle")}
          message={t("search.emptyMessage")}
        />
      ) : error && !isFetching ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : isFetching || isLoading ? (
        <Skeleton rows={6} />
      ) : (
        <div>
          <p className="atlas-muted mb-3">
            {t("search.results", { count: data?.total ?? 0, query })}
          </p>
          {(data?.items ?? []).map((group) => (
            <Card
              key={group.entityType}
              title={recordTypeLabel(t, group.entityType)}
              className="mb-3"
            >
              <table className="atlas-table">
                <tbody>
                  {group.results.map((r) => (
                    <tr key={r.id}>
                      <td style={{ inlineSize: 40 }}>
                        <Badge>{recordTypeLabel(t, group.entityType)}</Badge>
                      </td>
                      <td>
                        <strong>{r.title}</strong>
                        {r.subtitle && (
                          <div className="atlas-muted" style={{ fontSize: 13 }}>
                            {r.subtitle}
                          </div>
                        )}
                      </td>
                      {r.status && (
                        <td style={{ textAlign: "end" }}>
                          <Badge>
                            {statusLabel(t, group.entityType, r.status)}
                          </Badge>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}
          {(data?.items ?? []).length === 0 && (
            <EmptyState
              title={t("search.noResults")}
              message={t("search.noResultsHint")}
            />
          )}
        </div>
      )}
    </div>
  );
}
